import { useCallback, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';

/**
 * One query-string value as state: reading it, setting it (a history entry, so the browser's back button
 * undoes it) and clearing it. Client detail keeps every open detail panel in the URL this way, so a refresh or
 * a shared link reopens it and back closes it.
 */
export function useUrlParam(key: string): [string | null, (value: string | null) => void] {
  const [params, setParams] = useSearchParams();
  const value = params.get(key);
  const set = useCallback(
    (next: string | null) =>
      setParams((prev) => {
        const p = new URLSearchParams(prev);
        if (next == null) p.delete(key);
        else p.set(key, next);
        return p;
      }),
    [key, setParams],
  );
  return [value, set];
}

/**
 * Keep a detail panel in step with its URL value. `open(id)` runs when the URL names an id the panel isn't showing
 * (a refresh, a link, forward); `close()` runs when the value goes away (back). `shownId` is what the panel shows
 * now; `ready` holds the sync off until the rows the panel needs have loaded.
 */
export function useUrlPanelSync(urlId: string | null, shownId: string | null, ready: boolean, open: (id: string) => void, close: () => void) {
  const openRef = useRef(open);
  const closeRef = useRef(close);
  openRef.current = open;
  closeRef.current = close;
  useEffect(() => {
    if (!ready) return;
    if (urlId && urlId !== shownId) openRef.current(urlId);
    else if (!urlId && shownId) closeRef.current();
  }, [urlId, shownId, ready]);
}
